import { authenticate, jwtDecode, jwtIsSalesChannel } from '@commercelayer/js-auth'
import {
  getTokenCookie,
  removeTokenCookie,
  setTokenCookie,
} from '@/accounts/services/commerceLayer/utils/cookies'
import { createOrUpdateCustomer } from '@/utils/auth/create-update-customer'
import { config } from './config'
import { TOKEN_NAME } from './constants'

export type Token = {
  type: 'guest' | 'customer'
  accessToken: string
  customerId?: string
  scope?: string
  expires: number
}

export const getGuestAccessToken = () =>
  authenticate('client_credentials', {
    clientId: config.clientId,
    scope: config.scope,
  })

const getCustomerToken = async (customerId: string) => {
  const res = await fetch(`/api/commercelayer/customer/authenticate/${customerId}`)
  return await res?.json()
}

const processCustomerToken = (accessToken: string): Token | null => {
  if (!accessToken) return null
  try {
    const decodedToken = jwtDecode(accessToken)
    const { payload } = decodedToken

    if (jwtIsSalesChannel(payload)) {
      const expires = payload?.exp
      const customerId = payload.owner?.id

      const token: Token = {
        type: 'customer',
        customerId,
        accessToken,
        scope: config.scope,
        expires,
      }

      setTokenCookie(TOKEN_NAME, JSON.stringify(token), expires)

      return token
    }
  } catch (error) {
    console.error('Error decoding token:', error)
  }

  return null
}

export const hasValidJWTToken = () => {
  try {
    const token = getTokenCookie(TOKEN_NAME)

    if (token) {
      const { accessToken } = JSON.parse(token)
      const decodedToken = jwtDecode(accessToken)
      const expires = decodedToken?.payload?.exp as number
      const currentTime = Math.floor(Date.now() / 1000)

      if (!expires || expires < currentTime) {
        removeTokenCookie(TOKEN_NAME)
        return null
      }

      return JSON.parse(token)
    }
    return null
  } catch (error) {
    console.error('Error verifying token:', error)
    return null
  }
}

export const readCustomerToken = async (sub: string) => {
  try {
    const currentAccessToken = hasValidJWTToken()
    if (currentAccessToken?.type === 'customer') {
      return currentAccessToken
    }

    const res = await fetch(`/api/auth0/get-user/${sub}`)
    const user = await res?.json()

    let customerId = user?.user_metadata?.cl_customer_id
    if (!customerId) {
      const customer = await createOrUpdateCustomer(sub, user.email)
      if (!customer) throw new Error('Failed to get or create customer')
      customerId = customer?.user_metadata?.cl_customer_id
    }

    const { accessToken } = (await getCustomerToken(customerId)) || {}

    return processCustomerToken(accessToken)
  } catch (error) {
    console.error('Error in readCustomerToken:', error)
    return null
  }
}

export const readGuestToken = async () => {
  const currentAccessToken = hasValidJWTToken()
  if (currentAccessToken) {
    return currentAccessToken
  }

  const { accessToken, expiresIn } = await getGuestAccessToken()

  const token: Token = { type: 'guest', accessToken, scope: config.scope, expires: expiresIn }
  setTokenCookie(TOKEN_NAME, JSON.stringify(token), expiresIn)
  return token
}

export const getAccessToken = async (sub?: string) => {
  let token = null
  if (sub) {
    token = await readCustomerToken(sub)
  }

  if (!token) {
    token = await readGuestToken()
  }

  return token
}
